const ALL = [
  'console:view',
  // The default: everything not explicitly granted elsewhere.
  'console:admin',
  // One SDR, one pool. Not revoking every pool at once.
  'sdr-assignment:write',
  // Per-pool, and the sweep across every pool.
  'pool-open-to-all:write',
  // The manual dialer's dial pad: a number that matches no lead is still called (and
  // tracked as a placeholder lead). Every rostered SDR may dial a matched lead; only
  // these roles may dial past the match.
  'dialer:any-number',
  // Searching what was already dialed, across every pool and client. Named rather than
  // folded into `console:view` because it answers with the lead's own record — name,
  // number, email, employer — which the console otherwise holds back to `console:admin`.
  'call-history:read',
] as const;

// Derived from ALL so a new permission cannot be added to the union and then silently
// omitted from `admin`, which would 403 every admin on the new surface.
export type Permission = (typeof ALL)[number];

export type Role = 'admin' | 'sdr_manager' | 'user';

// Frozen because `readonly` is erased at runtime and this record is shared process-wide.
export const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = Object.freeze({
  admin: ALL,
  // Reads the console and works the dial pad, and holds no console write at all: the
  // job is watching the fleet and calling, not changing what the fleet dials — plus
  // finding the calls their team already made, which is how a callback gets worked.
  sdr_manager: ['console:view', 'dialer:any-number', 'call-history:read'],
  // Signed in and dialling; the console is simply not their surface.
  user: [],
});

export function isRole(role: string | undefined): role is Role {
  return role != null && Object.hasOwn(ROLE_PERMISSIONS, role);
}

// A typo in the hand-edited role column locks the person out rather than promoting them.
export function roleCan(role: string | undefined, permission: Permission): boolean {
  return isRole(role) && ROLE_PERMISSIONS[role].includes(permission);
}
